import { ChipButton, InlineIconButton } from "@/components/ui/pressable";
import { X } from "lucide-react";
import { useState } from "react";
import { useRecipientAutoCommit } from "@/lib/recipientAutoCommit";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { getClientRecipientOptions } from "./clientRecipientOptions";
import { Label } from "@/components/ui/label";
import {
  getInvalidInvoiceEmails,
  parseInvoiceEmailList,
  splitInvoiceListInput,
} from "@/modules/billing/billingUtils";
import {
  formatUsPhoneDisplayFromAny,
  isValidUsPhone,
  normalizeUsPhoneToE164,
} from "@/utils/phone";

type InvoiceRecipientChipInputProps = {
  id: string;
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  mode: "email" | "phone";
  placeholder?: string;
  helperText?: string;
  contact?: Contact | null;
  company?: Company | null;
  disabled?: boolean;
  /** Borderless, label-less row (used inside From/To compose rows). */
  inline?: boolean;
  /** Bordered field without its own label (the caller renders one). */
  hideLabel?: boolean;
};

const emailDuplicate = (values: string[], email: string) =>
  values.some((entry) => entry.toLowerCase() === email.toLowerCase());

const phoneDuplicate = (values: string[], phone: string) => {
  const e164 = normalizeUsPhoneToE164(phone);
  if (!e164) return false;
  return values.some((entry) => normalizeUsPhoneToE164(entry) === e164);
};

const normalizeEmailChip = (value: string) => {
  const invalid = getInvalidInvoiceEmails(value);
  if (invalid.length > 0) return null;
  return parseInvoiceEmailList(value)[0] ?? null;
};

const normalizePhoneChip = (value: string) => {
  if (!isValidUsPhone(value)) return null;
  const formatted = formatUsPhoneDisplayFromAny(value);
  return formatted === "—" ? value.trim() : formatted;
};

export const InvoiceRecipientChipInput = ({
  id,
  label,
  values,
  onChange,
  mode,
  placeholder,
  helperText,
  contact,
  company,
  disabled = false,
  inline = false,
  hideLabel = false,
}: InvoiceRecipientChipInputProps) => {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const suggestions = getClientRecipientOptions(mode, contact, company).filter(
    (value) =>
      !(mode === "email"
        ? emailDuplicate(values, value)
        : phoneDuplicate(values, value)),
  );

  const commit = (raw: string) => {
    if (disabled) return;
    setDraftError(null);
    const parts = splitInvoiceListInput(raw);
    if (parts.length === 0) return;

    const merged = [...values];
    for (const part of parts) {
      const normalized =
        mode === "email" ? normalizeEmailChip(part) : normalizePhoneChip(part);

      if (!normalized) {
        setDraftError(
          mode === "email"
            ? "Enter a valid email address"
            : "Enter a valid 10-digit US mobile number",
        );
        return;
      }

      const duplicate =
        mode === "email"
          ? emailDuplicate(merged, normalized)
          : phoneDuplicate(merged, normalized);

      if (!duplicate) {
        merged.push(normalized);
      }
    }

    onChange(merged);
    setDraft("");
  };

  // Complete addresses/numbers become chips without Enter or a click.
  useRecipientAutoCommit(draft, mode, commit, !disabled);

  return (
    <div className={inline ? "space-y-1.5" : "space-y-2"}>
      {inline || hideLabel ? null : <Label htmlFor={id}>{label}</Label>}
      <div
        className={
          inline
            ? "flex min-h-8 flex-wrap items-center gap-1.5"
            : "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring"
        }
      >
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
          >
            <span className="truncate">{value}</span>
            <InlineIconButton
              size="xs"
              disabled={disabled}
              aria-label={`Remove ${value}`}
              onClick={() =>
                onChange(values.filter((entry) => entry !== value))
              }
            >
              <X className="size-3" />
            </InlineIconButton>
          </span>
        ))}
        <input
          id={id}
          aria-label={inline ? label : undefined}
          disabled={disabled}
          className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          value={draft}
          placeholder={values.length === 0 ? placeholder : undefined}
          type={mode === "email" ? "text" : "tel"}
          inputMode={mode === "email" ? "email" : "tel"}
          autoComplete={mode === "email" ? "email" : "tel"}
          onChange={(event) => {
            setDraftError(null);
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" ||
              event.key === "," ||
              event.key === ";"
            ) {
              event.preventDefault();
              commit(draft);
            } else if (
              event.key === "Backspace" &&
              !draft &&
              values.length > 0
            ) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit(draft);
          }}
        />
      </div>
      {suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label="Saved recipients">
          {suggestions.map((value) => (
            <ChipButton
              key={value}
              size="sm"
              disabled={disabled}
              onClick={() => commit(value)}
              aria-label={`Add ${value}`}
            >
              + {mode === "phone" ? formatUsPhoneDisplayFromAny(value) : value}
            </ChipButton>
          ))}
        </div>
      ) : null}
      {helperText && !inline ? (
        <p className="text-xs text-muted-foreground">{helperText}</p>
      ) : null}
      {draftError ? (
        <p className="text-xs text-destructive">{draftError}</p>
      ) : null}
    </div>
  );
};
